import {
  collectDiscountEntries,
  couponLabel,
  resolveCouponFromDiscountRow,
  type DiscountRow,
} from '@/lib/stripeDiscounts';
import type { OfferingProducts, PlanProduct } from '@/types/billing';
import type { Stripe } from 'stripe';

/** A coupon limited to certain plans, as far as the plan check needs to know it. */
export interface ScopedCoupon {
  label: string;
  products: string[];
}

/** The products a coupon is limited to, or null when it works on any plan. */
export function couponProducts(coupon: Pick<Stripe.Coupon, 'applies_to'> | null | undefined): string[] | null {
  const products = coupon?.applies_to?.products;
  return products?.length ? products : null;
}

/** Every plan-limited coupon discounting these subscription/customer rows. */
export function scopedCouponsFrom(sources: (DiscountRow | null | undefined)[]): ScopedCoupon[] {
  const coupons: ScopedCoupon[] = [];
  for (const source of sources) {
    if (!source) continue;
    for (const entry of collectDiscountEntries(source)) {
      if (!entry || typeof entry !== 'object') continue;
      const coupon = resolveCouponFromDiscountRow(entry as DiscountRow);
      const products = couponProducts(coupon);
      if (products) coupons.push({ label: couponLabel(coupon), products });
    }
  }
  return coupons;
}

export interface CouponPlanMismatch {
  coupon: ScopedCoupon;
  /** The coupon's plans that can be bought today. Empty for a code tied to a retired plan. */
  plans: PlanProduct[];
}

/** The first coupon that can't discount `productId`, with the plans it can. */
export function findCouponPlanMismatch(
  coupons: ScopedCoupon[],
  productId: string | undefined,
  catalog: OfferingProducts | null | undefined,
): CouponPlanMismatch | null {
  if (!productId) return null;
  const coupon = coupons.find((c) => !c.products.includes(productId));
  if (!coupon) return null;
  const plans = coupon.products.map((id) => catalog?.[id]).filter((p): p is PlanProduct => Boolean(p));
  return { coupon, plans };
}
